import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { X, Download, Trash2, Plus, Receipt, Archive, Calendar, Ruler, Users2, FolderOpen, Mail, ChevronRight, MoreHorizontal, Clock, Check, ChevronUp, ChevronDown, FileText } from 'lucide-react';
import { Order } from '../lib/purchaseOrders';
import {
  updateOrderItemStatus,
  markAllItemsAsReceived,
  deleteOrder,
  closePurchaseOrder,
  createNewPOFromItem,
  updateOrderItemPrice,
  removeOrderItem,
  recordPurchaseOrderBill,
  getPurchaseOrderBills,
  setPurchaseOrderLinks,
  setPurchaseOrderReadyForPickup,
  getOrderEmailThread,
  linkEmailToOrder,
} from '../lib/purchaseOrders';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { ModalStackProvider, useModalZIndex } from '../contexts/ModalStackContext';
import { EmailDisplay } from './email/EmailDisplay';
import { EmailReplyComposer } from './email/EmailReplyComposer';
import ReceiveItemModal from './ReceiveItemModal';
import LinkOrderTargetModal, { OrderLinkSelection } from './LinkOrderTargetModal';
import { toast } from './toasts/SimpleToast';

export interface PurchaseOrderDetailModalProps {
  order: Order | null;
  onClose: () => void;
  onOrderUpdated: () => void | Promise<void>;
  business?: { name: string; address?: string | null; phone?: string | null; email?: string | null } | null;
}

function formatDate(dateString: string) {
  if (!dateString) return '';
  // date-only (yyyy-mm-dd) → local calendar day so UTC parsing doesn't shift the day
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString.slice(0, 10)) && dateString.length === 10) {
    const [y, m, d] = dateString.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function PurchaseOrderDetailModal({
  order,
  onClose,
  onOrderUpdated,
  business = null,
}: PurchaseOrderDetailModalProps) {
  const { selectedBusiness } = useBusiness();
  const { user } = useAuth();
  // Stack nested email thread above this PO modal. EmailDisplay (inside the
  // provider below) resolves to poModalZ + 10 — keep the PO-path composer above
  // that without touching EmailDisplay's global default.
  const poModalZ = useModalZIndex(100210);
  const emailComposeZ = poModalZ + 40;
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [poItemPriceEdit, setPoItemPriceEdit] = useState<Record<string, string>>({});
  const [poItemPriceSaving, setPoItemPriceSaving] = useState<string | null>(null);
  const [isDownloadingPoPdf, setIsDownloadingPoPdf] = useState(false);
  const [selectedOrderItem, setSelectedOrderItem] = useState<{
    id: string;
    orderNumber: string;
    name: string;
    unit: string;
    quantity: number;
    receivedQuantity: number;
  } | null>(null);
  const [isReceiveItemModalOpen, setIsReceiveItemModalOpen] = useState(false);
  const [bills, setBills] = useState<{ id: string; amount: number; expense_date: string; description: string }[]>([]);
  // Journey links resolved for display (lead → field quote → booking chain).
  const [linkedBooking, setLinkedBooking] = useState<{ id: string; label: string; date: string | null } | null>(null);
  const [linkedFieldQuote, setLinkedFieldQuote] = useState<{ id: string; label: string } | null>(null);
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);
  // Supplier email thread (outbound PO email + matched inbound replies).
  const [emailThread, setEmailThread] = useState<Awaited<ReturnType<typeof getOrderEmailThread>>>([]);
  const [showEmailThreadModal, setShowEmailThreadModal] = useState(false);
  const [emailComposeOpen, setEmailComposeOpen] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyToGmailId, setReplyToGmailId] = useState<string | null>(null);
  const [replyThreadId, setReplyThreadId] = useState<string | null>(null);
  const [replyInReplyTo, setReplyInReplyTo] = useState<string | null>(null);
  const [replyReferences, setReplyReferences] = useState<string | null>(null);
  const [showBillForm, setShowBillForm] = useState(false);
  const [billAmount, setBillAmount] = useState('');
  const [billDate, setBillDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [billSaving, setBillSaving] = useState(false);
  const [pickupDate, setPickupDate] = useState('');
  const [pickupSaving, setPickupSaving] = useState(false);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [emailsBillingOpen, setEmailsBillingOpen] = useState(true);
  useEffect(() => {
    if (!order) setPoItemPriceEdit({});
    setHeaderMenuOpen(false);
    // date inputs want yyyy-mm-dd; stored values are already date strings.
    setPickupDate(order?.ready_for_pickup_at ? String(order.ready_for_pickup_at).slice(0, 10) : '');
  }, [order?.id, order?.ready_for_pickup_at]);

  const billedTotal = bills.reduce((sum, b) => sum + (b.amount || 0), 0);
  const unbilled = Math.max(0, Math.round(((order?.total_amount || 0) - billedTotal) * 100) / 100);

  const loadBills = async (poId: string) => {
    try {
      setBills(await getPurchaseOrderBills(poId));
    } catch {
      setBills([]); // legacy orders without a backfilled PO simply show no bills
    }
  };

  useEffect(() => {
    setShowBillForm(false);
    setBills([]);
    if (order?.id && order.status !== 'cancelled') loadBills(order.id);
  }, [order?.id, order?.status]);

  // Resolve the linked booking / field quote for the "Linked to" section.
  useEffect(() => {
    setLinkedBooking(null);
    setLinkedFieldQuote(null);
    if (!supabase) return;
    if (order?.booking_id) {
      supabase
        .from('bookings')
        .select('id, title, booking_number, start_date')
        .eq('id', order.booking_id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            setLinkedBooking({
              id: data.id,
              label: data.booking_number ? `Booking #${data.booking_number}` : (data.title || 'Booking'),
              date: data.start_date ? formatDate(data.start_date) : null,
            });
          }
        });
    }
    if (order?.field_quote_id) {
      supabase
        .from('project_field_quotes')
        .select('id, field_quote_address, start_date')
        .eq('id', order.field_quote_id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            setLinkedFieldQuote({
              id: data.id,
              label: data.start_date ? `Field Quote · ${formatDate(data.start_date)}` : 'Field Quote',
            });
          }
        });
    }
  }, [order?.id, order?.booking_id, order?.field_quote_id]);

  // Manual re-link: overwrite the journey links with the user's pick.
  // The DB derives the rest of the chain (e.g. lead from field quote).
  const handleLinkSelected = async (sel: OrderLinkSelection) => {
    if (!order?.id) return;
    try {
      setLinkSaving(true);
      await setPurchaseOrderLinks(order.id, {
        bookingId: sel.type === 'booking' ? sel.id : null,
        fieldQuoteId: sel.type === 'field_quote' ? sel.id : null,
        pipelineOpportunitiesId: sel.type === 'lead' ? sel.id : null,
      });
      toast.success({
        title: sel.type === 'none' ? 'Link Removed' : 'Order Linked',
        description: sel.type === 'none'
          ? 'This order is no longer linked to a job.'
          : `Order linked to ${sel.label}.`,
        duration: 3000,
      });
      await Promise.resolve(onOrderUpdated());
    } catch (err) {
      console.error('Error updating order links:', err);
      toast.error({
        title: 'Link Failed',
        description: err instanceof Error ? err.message : 'Failed to update order link',
        duration: 4000,
      });
    } finally {
      setLinkSaving(false);
    }
  };

  const reloadEmailThread = async () => {
    if (!order?.id || !selectedBusiness?.id) return;
    try {
      const thread = await getOrderEmailThread(selectedBusiness.id, order.id);
      setEmailThread(thread);
    } catch (err) {
      console.error('Error reloading order email thread:', err);
    }
  };

  // Load the supplier email thread and mark unread replies as read (viewing
  // the order counts as seeing them — this clears the widget badges).
  useEffect(() => {
    setEmailThread([]);
    setShowEmailThreadModal(false);
    setEmailComposeOpen(false);
    if (!order?.id || !selectedBusiness?.id || !supabase) return;
    let stale = false;

    (async () => {
      try {
        const thread = await getOrderEmailThread(selectedBusiness.id, order.id!);
        if (stale) return;
        setEmailThread(thread);

        const unread = thread.filter((e) => e.direction === 'inbound' && e.read === 'unread');
        for (const entry of unread) {
          if (entry.all_messages_id) {
            await supabase
              .from('all_messages')
              .update({ read: 'read' })
              .eq('id', entry.all_messages_id)
              .eq('business_id', selectedBusiness.id);
          } else if (entry.external_id) {
            await supabase
              .from('all_messages')
              .update({ read: 'read' })
              .eq('twilio_message_sid', entry.external_id)
              .eq('business_id', selectedBusiness.id);
          }
        }
        if (unread.length > 0 && !stale) {
          setEmailThread((prev) => prev.map((e) => (e.direction === 'inbound' ? { ...e, read: 'read' as const } : e)));
          await Promise.resolve(onOrderUpdated());
        }
      } catch (err) {
        console.error('Error loading order email thread:', err);
      }
    })();

    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, selectedBusiness?.id]);

  /** Plain-text preview for email bodies that may contain HTML. */
  const emailPreview = (entry: { content: string; body_html: string | null }): string => {
    const raw = entry.content || entry.body_html || '';
    return raw
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Map PO thread → Messages EmailDisplay shape
  const emailThreadMessages = useMemo(
    () =>
      emailThread.map((e) => ({
        id: e.all_messages_id || e.id,
        subject: e.subject || undefined,
        body: e.body_html || e.content || '',
        created_at: e.sent_at || e.received_at || e.created_at,
        direction: e.direction as 'inbound' | 'outbound',
        read: (e.read || 'read') as 'read' | 'unread',
        from_number: e.direction === 'inbound' ? e.email_address : undefined,
        to_number: e.direction === 'outbound' ? e.email_address : undefined,
        twilio_message_sid: e.external_id || null,
      })),
    [emailThread]
  );

  const supplierEmail =
    order?.supplier?.email ||
    emailThread.find((e) => e.direction === 'inbound')?.email_address ||
    emailThread[0]?.email_address ||
    '';

  const latestEmail = emailThread[emailThread.length - 1];
  const latestInbound = [...emailThread].reverse().find((e) => e.direction === 'inbound') || latestEmail;

  // Resolve Gmail thread headers when opening the reply composer (same as Messages).
  useEffect(() => {
    if (!replyToGmailId || !selectedBusiness?.id || !supabase || !emailComposeOpen) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('received_emails')
          .select('thread_id, rfc_message_id, references_header')
          .eq('business_id', selectedBusiness.id)
          .eq('gmail_id', replyToGmailId)
          .maybeSingle();
        if (cancelled) return;
        if (error) {
          console.warn('Failed to fetch reply thread context:', error);
          return;
        }
        if (data) {
          setReplyThreadId(data.thread_id ?? null);
          setReplyInReplyTo(data.rfc_message_id ?? null);
          const refs = data.references_header?.trim();
          const msgId = data.rfc_message_id?.trim();
          setReplyReferences(refs && msgId ? `${refs} ${msgId}` : msgId ?? refs ?? null);
        } else {
          // Fall back to PO outgoing thread / message ids when the reply isn't in received_emails
          setReplyThreadId(order?.outgoing_email_thread_id ?? null);
          setReplyInReplyTo(order?.outgoing_email_message_id ?? replyToGmailId);
          setReplyReferences(order?.outgoing_email_message_id ?? replyToGmailId);
        }
      } catch (err) {
        if (!cancelled) console.warn('Error fetching reply thread:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [replyToGmailId, selectedBusiness?.id, emailComposeOpen, order?.outgoing_email_thread_id, order?.outgoing_email_message_id]);

  const closeEmailCompose = () => {
    setEmailComposeOpen(false);
    setReplySubject('');
    setReplyToGmailId(null);
    setReplyThreadId(null);
    setReplyInReplyTo(null);
    setReplyReferences(null);
  };

  const handleSavePickupDate = async (next: string | null) => {
    if (!order?.id || pickupSaving) return;
    setPickupSaving(true);
    try {
      await setPurchaseOrderReadyForPickup(order.id, next);
      setPickupDate(next || '');
      toast.success({
        title: next ? 'Pickup date saved' : 'Pickup date cleared',
        description: '',
        duration: 2000,
      });
      await Promise.resolve(onOrderUpdated());
    } catch (err) {
      console.error('Error saving ready-for-pickup date:', err);
      toast.error({ title: 'Could not save pickup date', description: '', duration: 3000 });
    } finally {
      setPickupSaving(false);
    }
  };

  const handleRecordBill = async () => {
    if (!order?.id) return;
    const amount = parseFloat(billAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      toast.error({ title: 'Invalid amount', description: 'Enter the billed amount from the supplier invoice.', duration: 3000 });
      return;
    }
    try {
      setBillSaving(true);
      await recordPurchaseOrderBill(order.id, {
        amountCents: Math.round(amount * 100),
        expenseDate: billDate,
      });
      await loadBills(order.id);
      setShowBillForm(false);
      setBillAmount('');
      toast.success({
        title: 'Bill Recorded',
        description: `$${amount.toFixed(2)} expense created for ${order.order_number}. Find it in the Expenses app.`,
        duration: 5000,
      });
    } catch (err) {
      console.error('Error recording bill:', err);
      toast.error({ title: 'Failed to record bill', description: err instanceof Error ? err.message : '', duration: 5000 });
    } finally {
      setBillSaving(false);
    }
  };

  const handleReceiveItem = async (itemId: string, orderNumber: string, itemName: string, quantity?: number) => {
    try {
      setLoadingOrders(true);
      if (quantity !== undefined) {
        await updateOrderItemStatus(itemId, 'Partially Received', quantity);
      } else {
        await updateOrderItemStatus(itemId, 'Received');
      }
      await Promise.resolve(onOrderUpdated());
      toast.success({ title: 'Item Received', description: `${itemName} has been marked as received.`, duration: 3000 });
    } catch (err) {
      console.error('Error receiving item:', err);
      toast.error({ title: 'Error', description: 'Failed to mark item as received', duration: 3000 });
    } finally {
      setLoadingOrders(false);
    }
  };

  const openReceiveItemModal = (item: { id?: string; item_name: string; item_unit: string; quantity: number; received_quantity?: number }, orderNumber: string) => {
    if (!item.id) return;
    setSelectedOrderItem({
      id: item.id,
      orderNumber,
      name: item.item_name,
      unit: item.item_unit,
      quantity: item.quantity,
      receivedQuantity: item.received_quantity || 0,
    });
    setIsReceiveItemModalOpen(true);
  };

  const handleReceiveFromModal = (quantity: number) => {
    if (!selectedOrderItem) return;
    const { id, orderNumber, name } = selectedOrderItem;
    if (quantity === selectedOrderItem.quantity - selectedOrderItem.receivedQuantity) {
      handleReceiveItem(id, orderNumber, name);
    } else {
      const newTotalReceived = selectedOrderItem.receivedQuantity + quantity;
      handleReceiveItem(id, orderNumber, name, newTotalReceived);
    }
    setIsReceiveItemModalOpen(false);
    setSelectedOrderItem(null);
  };

  const handleReceiveAllItems = async (orderId: string, orderNumber: string) => {
    try {
      if (!orderId) return;
      setLoadingOrders(true);
      await markAllItemsAsReceived(orderId);
      await Promise.resolve(onOrderUpdated());
      toast.success({ title: 'All Items Received', description: `All items from Order #${orderNumber} have been marked as received.`, duration: 3000 });
    } catch (err) {
      console.error('Error receiving all items:', err);
      toast.error({ title: 'Error', description: 'Failed to mark all items as received', duration: 3000 });
    } finally {
      setLoadingOrders(false);
    }
  };

  // Sent POs are cancelled (kept for the audit trail); drafts are deleted —
  // matches how QuickBooks/NetSuite treat issued vs. unsent purchase orders.
  const handleCancelOrder = async (orderId: string) => {
    try {
      if (!window.confirm('Cancel this purchase order? It will be kept in your records but marked as cancelled.')) return;
      setLoadingOrders(true);
      await deleteOrder(orderId);
      await Promise.resolve(onOrderUpdated());
      toast.info({ title: 'Order Cancelled', description: 'The purchase order has been cancelled.', duration: 3000 });
      onClose();
    } catch (err) {
      console.error('Error cancelling order:', err);
      if (err instanceof Error && err.message.toLowerCase().includes('received')) {
        toast.error({ title: 'Cannot Cancel Order', description: 'Items have already been received against this order. Receive or remove the remaining items instead.', duration: 5000 });
      } else {
        toast.error({ title: 'Error', description: 'Failed to cancel order', duration: 3000 });
      }
    } finally {
      setLoadingOrders(false);
    }
  };

  // "Close short" — the undelivered remainder is never coming; stop expecting it.
  // Received quantities stay on record and the PO moves to a final state.
  const handleCloseOrder = async (orderId: string) => {
    try {
      if (!window.confirm('Close this order? Items already received stay on record, and the remaining unreceived quantity will no longer be expected.')) return;
      setLoadingOrders(true);
      await closePurchaseOrder(orderId);
      await Promise.resolve(onOrderUpdated());
      toast.success({ title: 'Order Closed', description: 'The remaining quantity is no longer expected.', duration: 4000 });
      onClose();
    } catch (err) {
      console.error('Error closing order:', err);
      toast.error({ title: 'Failed to close order', description: err instanceof Error ? err.message : '', duration: 4000 });
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleCreateNewPO = async (orderId: string, orderItemId: string, itemName: string) => {
    try {
      if (!selectedBusiness?.id) {
        toast.error({ title: 'Error', description: 'Business not selected.', duration: 3000 });
        return;
      }
      if (!window.confirm(`Backorder "${itemName}"? The unreceived quantity moves to a new PO so this order can be completed.`)) return;
      const result = await createNewPOFromItem(orderId, orderItemId, selectedBusiness.id);
      if (result.success) {
        await Promise.resolve(onOrderUpdated());
        toast.success({ title: 'New PO Created', description: `New PO ${result.newOrderNumber} created for "${itemName}".`, duration: 5000 });
      } else {
        toast.error({ title: 'Error', description: result.error || 'Failed to create new PO.', duration: 5000 });
      }
    } catch (error) {
      console.error('Error creating new PO:', error);
      toast.error({ title: 'Error', description: 'Failed to create new PO. Please try again.', duration: 5000 });
    }
  };

  // Server-side PDF from the purchase_orders ledger — same document that was
  // attached to the supplier email (generate-purchase-order-pdf, like invoices).
  const handleDownloadPoPdf = async () => {
    if (!order) return;
    try {
      setIsDownloadingPoPdf(true);
      const { data, error } = await supabase.functions.invoke('generate-purchase-order-pdf', {
        body: { purchaseOrderId: order.id },
      });
      if (error || !data?.base64) {
        throw new Error(data?.error || 'PDF generation failed');
      }

      const binaryStr = atob(data.base64);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const filename = data.filename || `${order.order_number}.pdf`;

      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const file = new File([blob], filename, { type: 'application/pdf' });
        if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: filename }).catch(() => {});
          return;
        }
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Delayed revoke: revoking synchronously can silently cancel the download.
      window.setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      console.error('Error generating PO PDF:', err);
      toast.error({ title: 'Failed to download PDF', description: '', duration: 3000 });
    } finally {
      setIsDownloadingPoPdf(false);
    }
  };

  if (!order) return null;

  const items = order.items || [];
  const isQuoteRequest = items.length > 0 && items.some((i) => !i.unit_price || i.unit_price <= 0);
  const receivedItems = items.filter((i) => (i.received_quantity ?? 0) >= i.quantity).length;
  const allReceived = items.length > 0 && receivedItems === items.length;
  const hasAnyReceipt = items.some((i) => (i.received_quantity ?? 0) > 0);
  const totalDisplay = order.total_amount === 0 && isQuoteRequest ? 'TBD' : `$${order.total_amount.toFixed(2)}`;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return createPortal(
    <ModalStackProvider zIndex={poModalZ}>
    <>
      <div
        className="fixed inset-0 bg-black/50"
        style={{
          zIndex: poModalZ,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className={`bg-white ${
            isMobile ? 'fixed left-0 right-0 w-full' : 'fixed inset-y-0 right-0 w-full md:w-[560px] shadow-2xl'
          }`}
          style={{
            zIndex: poModalZ + 1,
            top: 'env(safe-area-inset-top, 0px)',
            bottom: 'env(safe-area-inset-bottom, 0px)',
            ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
          }}
          role="dialog"
          aria-label="Purchase order details"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-full flex flex-col overflow-hidden">
            {/* Header — matches the customer order details modal */}
            <div className="bg-gray-50 border-b border-gray-200 px-5 py-4 flex items-center justify-between flex-shrink-0">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-[17px] font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                    {order.order_number}
                  </h3>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                      order.status === 'pending' ? 'bg-amber-100 text-amber-700' : order.status === 'received' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
                    }`}
                  >
                    {order.status === 'pending' ? <Clock className="w-3 h-3" /> : order.status === 'received' ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    {order.status === 'pending' ? 'Pending' : order.status === 'received' ? 'Received' : 'Cancelled'}
                  </span>
                </div>
                <p className="text-[12px] text-gray-400 mt-0.5 truncate">
                  {`${order.supplier?.first_name ?? ''} ${order.supplier?.last_name ?? ''}`.trim() || 'Unknown Supplier'}
                  {' · '}
                  {formatDate(order.created_at || '')}
                  {' · '}
                  {isQuoteRequest ? 'Quote request' : 'Purchase order'}
                </p>
              </div>
              <div className="flex items-center flex-shrink-0">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setHeaderMenuOpen((v) => !v)}
                    className="p-2 rounded-full hover:bg-gray-100 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    aria-label="More actions"
                  >
                    <MoreHorizontal className="w-5 h-5 text-gray-500" />
                  </button>
                  {headerMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setHeaderMenuOpen(false)} />
                      <div className="absolute right-0 top-full mt-1 w-52 rounded-2xl border border-gray-200 bg-white shadow-xl py-1.5 z-20">
                        <button
                          type="button"
                          onClick={() => { setHeaderMenuOpen(false); void handleDownloadPoPdf(); }}
                          disabled={isDownloadingPoPdf}
                          className="w-full px-4 py-2.5 text-left text-[13px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2.5"
                        >
                          <Download className="w-4 h-4 text-gray-400" />
                          {isDownloadingPoPdf ? 'Preparing…' : 'Download PDF'}
                        </button>
                        {/* Something received → cancel is off the table (receipts exist);
                            offer "close short" instead, which stops expecting the rest. */}
                        {order.status === 'pending' && hasAnyReceipt && (
                          <button
                            type="button"
                            onClick={() => { setHeaderMenuOpen(false); order.id && handleCloseOrder(order.id); }}
                            disabled={loadingOrders}
                            className="w-full px-4 py-2.5 text-left text-[13px] font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50 flex items-center gap-2.5"
                            title="Stop expecting the remaining unreceived quantity"
                          >
                            <Archive className="w-4 h-4" />
                            Close order
                          </button>
                        )}
                        {order.status === 'pending' && !hasAnyReceipt && (
                          <button
                            type="button"
                            onClick={() => { setHeaderMenuOpen(false); order.id && handleCancelOrder(order.id); }}
                            disabled={loadingOrders}
                            className="w-full px-4 py-2.5 text-left text-[13px] font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 flex items-center gap-2.5"
                          >
                            <Trash2 className="w-4 h-4" />
                            Cancel order
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-gray-50/50" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="px-4 md:px-5 py-4 space-y-4">
                {/* Overview — totals, pickup date, linked job */}
                <section className="space-y-2">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">Overview</h4>
                  <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] divide-y divide-gray-100">
                    <div className="p-4 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-medium text-gray-400">Total</p>
                        <p className="text-xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>{totalDisplay}</p>
                      </div>
                      {items.length > 0 && (
                        <div className="text-right">
                          <p className="text-[11px] font-medium text-gray-400">Received</p>
                          <p className={`text-[15px] font-bold ${allReceived ? 'text-green-600' : 'text-red-600'}`}>
                            {receivedItems} of {items.length}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Ready for pickup — blank until the business sets a date */}
                    <div className="p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-[13px] font-semibold text-gray-900">Ready for pickup</p>
                          <p className="text-[12px] text-gray-400">Expected date from supplier</p>
                        </div>
                        {pickupDate && order.status !== 'cancelled' && (
                          <button
                            type="button"
                            onClick={() => void handleSavePickupDate(null)}
                            disabled={pickupSaving}
                            className="text-[12px] font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50 touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      {order.status === 'cancelled' ? (
                        <p className="mt-2 text-sm text-gray-400">
                          {pickupDate ? formatDate(pickupDate) : 'Not set'}
                        </p>
                      ) : (
                        <div className="flex items-center gap-2 mt-2.5">
                          <input
                            type="date"
                            value={pickupDate}
                            disabled={pickupSaving}
                            onChange={(e) => {
                              const value = e.target.value;
                              setPickupDate(value);
                              void handleSavePickupDate(value || null);
                            }}
                            className="flex-1 px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-200 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 disabled:opacity-50"
                          />
                          {!pickupDate && (
                            <span className="text-xs text-gray-400 flex-shrink-0">Not set</span>
                          )}
                        </div>
                      )}
                      {pickupDate && order.status !== 'cancelled' && (
                        <p className="mt-2 text-xs font-medium text-emerald-700">
                          Ready for pickup {formatDate(pickupDate)}
                        </p>
                      )}
                    </div>

                    {/* Journey links — where this PO sits in the customer chain */}
                    {(() => {
                      const hasAnyLink = Boolean(order.project || linkedBooking || linkedFieldQuote || order.pipeline_opportunities_id);
                      return (
                        <div className="p-4">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="text-[13px] font-semibold text-gray-900">Linked job</p>
                              <p className="text-[12px] text-gray-400">Job this PO is tied to</p>
                            </div>
                            {order.status !== 'cancelled' && (
                              <button
                                onClick={() => setShowLinkPicker(true)}
                                disabled={linkSaving}
                                className="text-[12px] font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50 touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                {linkSaving ? 'Saving…' : hasAnyLink ? 'Edit' : '+ Link'}
                              </button>
                            )}
                          </div>
                          <div className="mt-2.5 flex flex-wrap gap-1.5">
                            {linkedBooking && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200/60 rounded-full">
                                <Calendar className="h-3.5 w-3.5" />
                                {linkedBooking.label}
                                {linkedBooking.date && <span className="text-blue-500">· {linkedBooking.date}</span>}
                              </span>
                            )}
                            {linkedFieldQuote && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200/60 rounded-full">
                                <Ruler className="h-3.5 w-3.5" />
                                {linkedFieldQuote.label}
                              </span>
                            )}
                            {order.project && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200/60 rounded-full">
                                <FolderOpen className="h-3.5 w-3.5" />
                                {order.project.title}
                              </span>
                            )}
                            {order.pipeline_opportunities_id && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200/60 rounded-full">
                                <Users2 className="h-3.5 w-3.5" />
                                CRM Lead
                              </span>
                            )}
                            {!linkedBooking && (
                              <span className="inline-flex items-center px-3 py-1.5 text-xs text-gray-400 bg-gray-50 border border-dashed border-gray-200 rounded-full">
                                {hasAnyLink
                                  ? 'Booking attaches automatically when the lead converts'
                                  : 'Not linked to a job yet — tap + Link to attach one'}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </section>

                {/* Items */}
                <section className="space-y-2">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">Items ({items.length})</h4>
                  <div className="space-y-2.5">
                    {items.map((item) => {
                      const hasReceived = (item.received_quantity ?? 0) > 0;
                      // Post-send, cost is only editable on lines with nothing received
                      // (matches the DB rule — received lines are financially locked).
                      const canEditPrice = order.status === 'pending' && !hasReceived;
                      const specLines = (item.notes || '').split('\n').map((l) => l.trim()).filter(Boolean);
                      return (
                        <div key={item.id} className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] p-4">
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-[13px] font-semibold text-gray-900">{item.item_name}</span>
                            {item.status === 'Not Received' ? (
                              <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-600">{item.quantity} pending</span>
                            ) : item.status === 'Partially Received' ? (
                              <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700">
                                {item.received_quantity || 0} of {item.quantity} received
                              </span>
                            ) : (
                              <span className="flex-shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-700">Received</span>
                            )}
                          </div>
                          {specLines.length > 0 && (
                            <ul className="mt-1.5 space-y-0.5">
                              {specLines.map((line, i) => (
                                <li key={i} className="flex items-start gap-1.5 text-[12px] text-gray-500">
                                  <span className="text-gray-300 leading-[18px] flex-shrink-0">•</span>
                                  <span className="whitespace-pre-wrap">{line}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {item.images && item.images.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {item.images.map((url) => (
                                <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                                  <img
                                    src={url}
                                    alt="Reference"
                                    className="h-12 w-12 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity"
                                  />
                                </a>
                              ))}
                            </div>
                          )}
                          <div className="mt-2.5 text-[12px] text-gray-600 flex items-center gap-1.5 flex-wrap">
                            <span>Qty: {item.quantity}</span>
                            <span className="text-gray-300">·</span>
                            {canEditPrice ? (
                              <span className="flex items-center gap-1">
                                <span className="inline-flex items-center gap-0.5 px-2 py-1 bg-white border border-gray-300 rounded-lg">
                                  <span className="text-gray-500">$</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="w-16 text-[12px] text-gray-900 bg-transparent outline-none"
                                    value={poItemPriceEdit[item.id!] ?? item.unit_price.toFixed(2)}
                                    onChange={(e) => setPoItemPriceEdit((prev) => ({ ...prev, [item.id!]: e.target.value }))}
                                    onBlur={async () => {
                                      if (!item.id) return;
                                      const raw = poItemPriceEdit[item.id];
                                      if (raw === undefined) return;
                                      const next = parseFloat(raw);
                                      if (Number.isNaN(next) || next < 0) {
                                        setPoItemPriceEdit((prev) => { const p = { ...prev }; delete p[item.id!]; return p; });
                                        return;
                                      }
                                      if (Math.abs(next - item.unit_price) < 0.005) {
                                        setPoItemPriceEdit((prev) => { const p = { ...prev }; delete p[item.id!]; return p; });
                                        return;
                                      }
                                      setPoItemPriceSaving(item.id);
                                      try {
                                        await updateOrderItemPrice(item.id, next);
                                        setPoItemPriceEdit((prev) => { const p = { ...prev }; delete p[item.id]; return p; });
                                        await Promise.resolve(onOrderUpdated());
                                      } catch (err) {
                                        toast.error({ title: 'Failed to update price', description: '', duration: 3000 });
                                      } finally {
                                        setPoItemPriceSaving(null);
                                      }
                                    }}
                                    disabled={poItemPriceSaving === item.id}
                                    aria-label="Unit price"
                                  />
                                </span>
                                <span>/unit</span>
                                <span className="text-gray-300">·</span>
                                <span>
                                  Total: <span className="font-semibold text-gray-900">
                                    ${(item.quantity * (() => { const raw = poItemPriceEdit[item.id!]; if (raw === undefined || raw === '') return item.unit_price; const n = parseFloat(raw); return Number.isNaN(n) ? item.unit_price : n; })()).toFixed(2)}
                                  </span>
                                </span>
                              </span>
                            ) : (
                              <span>
                                {item.unit_price > 0
                                  ? <>${item.unit_price.toFixed(2)}/unit <span className="text-gray-300">·</span> Total: <span className="font-semibold text-gray-900">${(item.quantity * item.unit_price).toFixed(2)}</span></>
                                  : 'TBD — awaiting supplier pricing'}
                              </span>
                            )}
                          </div>
                          {order.status === 'pending' && (
                            <div className="mt-3 flex gap-2 flex-wrap">
                              {item.status !== 'Received' && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (item.id && order.order_number) {
                                        if (item.quantity === 1) {
                                          handleReceiveItem(item.id, order.order_number, item.item_name);
                                          Promise.resolve(onOrderUpdated());
                                        } else {
                                          openReceiveItemModal(item, order.order_number);
                                        }
                                      }
                                    }}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 active:scale-[0.98] transition-all duration-150 touch-manipulation"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    <Check className="h-3.5 w-3.5" />
                                    {item.quantity === 1 ? 'Receive' : 'Receive…'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => item.id && order.id && handleCreateNewPO(order.id, item.id, item.item_name)}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 active:scale-[0.98] transition-all duration-150 touch-manipulation"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                    title="Move the unreceived quantity to a new PO"
                                  >
                                    <Plus className="h-3.5 w-3.5" />
                                    Backorder
                                  </button>
                                </>
                              )}
                              {/* Received lines are locked — no removal, matching real PO systems */}
                              {!hasReceived && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!item.id) return;
                                    if (!confirm(`Remove "${item.item_name}" from this order?`)) return;
                                    try {
                                      await removeOrderItem(item.id);
                                      await Promise.resolve(onOrderUpdated());
                                      const remaining = items.filter((i) => i.id !== item.id);
                                      if (remaining.length === 0) onClose();
                                    } catch (err) {
                                      toast.error({ title: 'Failed to remove item', description: '', duration: 3000 });
                                    }
                                  }}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] font-semibold text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 active:scale-[0.98] transition-all duration-150 touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                  title="Remove from order"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                  Remove
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>

                {/* Emails & billing — supplier thread + bill → expense in one collapsible card
                    (PO is non-posting; the bill is what hits the books) */}
                {(emailThread.length > 0 || order.status !== 'cancelled') && (
                  <section className="space-y-2">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-1">Emails &amp; Billing</h4>
                    <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setEmailsBillingOpen((v) => !v)}
                        className="w-full px-4 py-3 flex items-center justify-between gap-2 touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <span className="text-[11px] font-bold text-gray-700 uppercase tracking-wide">Emails &amp; Billing</span>
                        <span className="flex items-center gap-1.5">
                          {emailThread.length > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600">
                              <Mail className="w-3 h-3" />
                              {emailThread.length} {emailThread.length === 1 ? 'email' : 'emails'}
                            </span>
                          )}
                          {order.status !== 'cancelled' && (
                            billedTotal > 0 ? (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${unbilled === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                <Receipt className="w-3 h-3" />
                                {unbilled === 0 ? 'Billed' : 'Partially billed'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-600">
                                <Receipt className="w-3 h-3" />
                                Not billed
                              </span>
                            )
                          )}
                          {emailsBillingOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </span>
                      </button>
                      {emailsBillingOpen && (
                        <div className="px-4 pb-4 pt-3 border-t border-gray-100 space-y-4">
                          {/* Supplier email thread — tap opens the Messages email thread modal */}
                          {emailThread.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Emails · {emailThread.length}</p>
                              {emailThread.map((entry) => {
                                const preview = emailPreview(entry);
                                return (
                                  <button
                                    key={entry.id}
                                    type="button"
                                    onClick={() => setShowEmailThreadModal(true)}
                                    className="w-full text-left bg-gray-50 rounded-[10px] p-3 hover:bg-gray-100 transition-colors touch-manipulation"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    <div className="flex items-center gap-2">
                                      <Mail className={`h-3.5 w-3.5 flex-shrink-0 ${entry.direction === 'inbound' ? 'text-blue-600' : 'text-gray-400'}`} />
                                      <span className={`text-xs font-semibold ${entry.direction === 'inbound' ? 'text-blue-700' : 'text-gray-600'}`}>
                                        {entry.direction === 'inbound' ? 'Supplier reply' : 'Sent to supplier'}
                                      </span>
                                      <span className="text-xs text-gray-400 truncate flex-1">{entry.email_address}</span>
                                      <span className="text-xs text-gray-400 flex-shrink-0">
                                        {formatDate(entry.received_at || entry.sent_at || entry.created_at)}
                                      </span>
                                      <ChevronRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                    </div>
                                    {entry.subject && (
                                      <p className="mt-1.5 text-sm font-medium text-gray-900 truncate">{entry.subject}</p>
                                    )}
                                    {preview && (
                                      <p className="mt-1 text-xs text-gray-500 whitespace-pre-wrap line-clamp-2">
                                        {preview}
                                      </p>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {/* Supplier bill → expense */}
                          {order.status !== 'cancelled' && (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Supplier Bill</p>
                                {!showBillForm && unbilled > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => { setBillAmount(unbilled.toFixed(2)); setShowBillForm(true); }}
                                    className="text-[12px] font-semibold text-blue-600 hover:text-blue-700 touch-manipulation"
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    Record bill
                                  </button>
                                )}
                              </div>
                              {billedTotal > 0 ? (
                                <p className={`text-xs font-medium ${unbilled === 0 ? 'text-green-700' : 'text-amber-700'}`}>
                                  ${billedTotal.toFixed(2)} of ${order.total_amount.toFixed(2)} billed
                                </p>
                              ) : (
                                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-[10px] px-3 py-2.5">
                                  <FileText className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                                  <p className="text-[12px] text-amber-800">Not billed yet — record the supplier's invoice to create the expense.</p>
                                </div>
                              )}
                              {showBillForm && (
                                <div className="flex flex-wrap items-end gap-2">
                                  <label className="text-xs text-gray-600">
                                    Amount
                                    <div className="flex items-center gap-1 mt-0.5">
                                      <span className="text-sm text-gray-700">$</span>
                                      <input
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        value={billAmount}
                                        onChange={(e) => setBillAmount(e.target.value)}
                                        className="w-24 px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white"
                                      />
                                    </div>
                                  </label>
                                  <label className="text-xs text-gray-600">
                                    Bill date
                                    <input
                                      type="date"
                                      value={billDate}
                                      onChange={(e) => setBillDate(e.target.value)}
                                      className="block mt-0.5 px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white"
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    onClick={handleRecordBill}
                                    disabled={billSaving}
                                    className="px-3 py-2 text-xs font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50"
                                  >
                                    {billSaving ? 'Saving…' : 'Create Expense'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setShowBillForm(false)}
                                    className="px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              )}
                              {bills.length > 0 && (
                                <ul className="space-y-1">
                                  {bills.map((b) => (
                                    <li key={b.id} className="text-xs text-gray-600 flex justify-between">
                                      <span className="truncate">{b.description}</span>
                                      <span className="font-medium text-gray-900 whitespace-nowrap ml-2">
                                        ${b.amount.toFixed(2)} · {b.expense_date}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </section>
                )}
              </div>
            </div>

            {/* Fixed bottom — totals left, actions right (matches order details footer);
                cancel / close-short live in the header ⋯ menu */}
            <div className="flex-shrink-0 border-t border-gray-100 bg-white px-4 md:px-5 py-3">
              <div className="flex items-center gap-4 flex-wrap">
                <div>
                  <p className="text-[11px] font-medium text-gray-400">Total</p>
                  <p className="text-[15px] font-bold text-gray-900 leading-tight">{totalDisplay}</p>
                </div>
                {items.length > 0 && (
                  <div>
                    <p className="text-[11px] font-medium text-gray-400">Received</p>
                    <p className={`text-[15px] font-bold leading-tight ${allReceived ? 'text-green-600' : 'text-red-600'}`}>
                      {receivedItems} of {items.length}
                    </p>
                  </div>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleDownloadPoPdf}
                    disabled={isDownloadingPoPdf}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2.5 text-[12px] font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-100 active:scale-[0.98] disabled:opacity-50 transition-all duration-150 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Download className="h-4 w-4" />
                    {isDownloadingPoPdf ? 'Preparing…' : 'Download PDF'}
                  </button>
                  {order.status === 'pending' && !allReceived && (
                    <button
                      type="button"
                      onClick={() => order.id && order.order_number && handleReceiveAllItems(order.id, order.order_number)}
                      disabled={loadingOrders}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-semibold text-white bg-gray-900 rounded-xl hover:bg-gray-800 active:scale-[0.98] disabled:opacity-50 transition-all duration-150 touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Check className="h-4 w-4" />
                      Receive all items
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedOrderItem && (
        <ReceiveItemModal
          isOpen={isReceiveItemModalOpen}
          onClose={() => { setIsReceiveItemModalOpen(false); setSelectedOrderItem(null); }}
          itemName={selectedOrderItem.name}
          itemUnit={selectedOrderItem.unit}
          totalQuantity={selectedOrderItem.quantity}
          remainingQuantity={selectedOrderItem.quantity - selectedOrderItem.receivedQuantity}
          onReceive={handleReceiveFromModal}
        />
      )}

      {/* Journey link picker (add or change the linked job) */}
      <LinkOrderTargetModal
        isOpen={showLinkPicker}
        onClose={() => setShowLinkPicker(false)}
        onSelect={handleLinkSelected}
        allowUnlink={Boolean(order.booking_id || order.field_quote_id || order.pipeline_opportunities_id)}
      />

      {/* Same Messages email-thread modal (EmailDisplay) — reply from here too */}
      {showEmailThreadModal && latestEmail && selectedBusiness?.id && (
        <div className="fixed w-0 h-0 overflow-hidden opacity-0 pointer-events-none" aria-hidden>
          <EmailDisplay
            key={`po-email-${order.id}-${latestEmail.id}`}
            content={latestEmail.body_html || latestEmail.content || ''}
            subject={latestEmail.subject || `Order ${order.order_number}`}
            createdAt={latestEmail.sent_at || latestEmail.received_at || latestEmail.created_at}
            compactView
            isOutbound={latestEmail.direction === 'outbound'}
            replyTo={supplierEmail}
            replySubject={
              (latestEmail.subject || `Order ${order.order_number}`)
                .replace(/^(re:|fwd:|fw:)\s*/gi, '')
                .trim()
            }
            businessId={selectedBusiness.id}
            customerId={order.supplier_id || null}
            replyToGmailId={latestInbound?.external_id || order.outgoing_email_message_id || null}
            threadMessages={emailThreadMessages}
            userEmail={user?.email ?? undefined}
            initialOpenFullEmail
            onFullEmailOpened={() => setShowEmailThreadModal(false)}
            onReplyClick={(replyTo, replySubjectVal, gmailId) => {
              const to = replyTo || supplierEmail;
              if (!to) {
                toast.error({ title: 'No supplier email', description: 'Add an email on the supplier to reply.', duration: 3000 });
                return;
              }
              setReplySubject(
                replySubjectVal.startsWith('Re:') ? replySubjectVal : `Re: ${replySubjectVal}`
              );
              setReplyToGmailId(gmailId ?? latestInbound?.external_id ?? order.outgoing_email_message_id ?? null);
              setReplyThreadId(order.outgoing_email_thread_id ?? null);
              setReplyInReplyTo(null);
              setReplyReferences(null);
              setEmailComposeOpen(true);
            }}
          />
        </div>
      )}

      {emailComposeOpen && selectedBusiness?.id && supplierEmail && createPortal(
        <div
          className="fixed inset-0 transition-opacity duration-200 bg-black/50"
          style={{
            zIndex: emailComposeZ,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          onClick={closeEmailCompose}
        >
          <div
            className="bg-white transform transition-all duration-200 fixed inset-y-0 right-0 w-full sm:w-[560px] shadow-2xl flex flex-col translate-x-0"
            style={{
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50/60 flex-shrink-0">
              <div className="text-sm font-semibold text-gray-800">Reply</div>
              <button
                type="button"
                onClick={closeEmailCompose}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-4 flex-shrink-0">
              <div className="flex items-center gap-2 border-b border-gray-200 py-2">
                <div className="text-base text-gray-500 w-10">To</div>
                <input
                  className="flex-1 text-base text-gray-800 bg-transparent outline-none"
                  style={{ fontSize: '16px' }}
                  value={supplierEmail}
                  readOnly
                />
              </div>
              {replySubject && (
                <div className="flex items-center gap-2 border-b border-gray-200 py-2">
                  <div className="text-base text-gray-500 w-10">Subject</div>
                  <input
                    className="flex-1 text-base text-gray-800 bg-gray-50 outline-none cursor-not-allowed"
                    style={{ fontSize: '16px' }}
                    value={replySubject}
                    readOnly
                  />
                </div>
              )}
            </div>
            <div className="flex-1 min-h-0 flex flex-col relative">
              <EmailReplyComposer
                businessId={selectedBusiness.id}
                to={supplierEmail}
                defaultSubject={replySubject}
                placeholder="Compose"
                onSendSuccess={async (messageId, details) => {
                  try {
                    if (messageId && order.id && order.supplier_id) {
                      await linkEmailToOrder(
                        order.id,
                        selectedBusiness.id,
                        order.supplier_id,
                        supplierEmail,
                        details?.subject || replySubject,
                        details?.body || '',
                        messageId
                      );
                    }
                    await reloadEmailThread();
                    await Promise.resolve(onOrderUpdated());
                    toast.success({ title: 'Reply sent', description: '', duration: 2000 });
                  } catch (err) {
                    console.error('Error linking PO reply:', err);
                  }
                  closeEmailCompose();
                }}
                onSendError={(error) => {
                  toast.error({ title: 'Failed to send', description: error || 'Could not send reply', duration: 4000 });
                }}
                className="flex-1 min-h-0"
                variant="gmail"
                customerId={order.supplier_id}
                shouldFetchAI={false}
                threadId={replyThreadId ?? undefined}
                inReplyTo={replyInReplyTo ?? undefined}
                references={replyReferences ?? undefined}
                showSubjectInput={false}
                subjectReadOnly
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
    </ModalStackProvider>,
    document.body
  );
}
